/* eslint-disable no-unused-vars */
/* eslint-disable max-len */

import { PolymerElement } from "@polymer/polymer/polymer-element.js";
import { html } from "@polymer/polymer/lib/utils/html-tag.js";
import template from "./ns-accordion-item.html";

import "@nuskin/ns-style-elements";

/**
 * <p>The ns-accordion-item can be used alone or inside ns-accordion. Wrapping several of them in
 *    ns-accordion will give you ways to open/close all items or only allow one to be open at a time.</p>
 *
 * <h2>UX/Design Docs</h2>
 * <a href="https://nuskin.invisionapp.com/d/main/#/console/13401975/279861734/inspect" target="_blank">
 *   <img class="ux-doc" src="https://nuskin.invisionapp.com/static-signed/live-embed/242207610/291557363/1/latest/LCYLlE5UvRv1hPLlbltPXj0be9rPrIuv4WVHfEbG9KYZUsXZvYMyA36gaMkeR60S9D2QkoKCn8dWK3aWJ1vfHtQlE/Accordion-2x.png" alt="Accordion Item UX Documentation">
 * </a>
 *
 * <h2>Demo</h2>
 * <demo-snippet class="demoSnippet">
 *   <template>
 *     <ns-accordion-item>Example Accordion Item</ns-accordion-item>
 *     <ns-accordion-item opened>
 *       <p slot="panel-title">Slotted Title</p>
 *       <p>This item is open when the page loades because of the opened property</p>
 *       <p>Plus it has sub-items</p>
 *       <ns-accordion-item title="Sub Item" level="second">
 *           <p>some stuff 1</p>
 *           <p>some stuff 2</p>
 *           <p>some stuff 3</p>
 *           <p>some stuff 4</p>
 *           <p>some stuff 5</p>
 *       </ns-accordion-item>
 *       <ns-accordion-item title="Another Sub Item's Title" level="second">
 *         <p>some other stuff</p>
 *       </ns-accordion-item>
 *     </ns-accordion-item>
 *     <ns-accordion-item title="Other Title">
 *       <p>Some longer text content.</p>
 *       <p>Lorem ipsum dolor sit amet consectetur adipisicing elit. Corrupti culpa corporis fuga recusandae. Commodi, eum quisquam omnis vitae modi illum sit, nam ad deleniti incidunt, impedit dolor? Beatae, odio porro?</p>
 *       <p>Eligendi odit provident consectetur voluptatibus consequuntur! Veritatis in maiores explicabo velit quaerat iste, labore pariatur fugiat quod odit ipsam error cumque laborum beatae hic dolor? Deserunt, veniam. Sed, deserunt consequatur.</p>
 *       <p>Sapiente accusantium impedit iste inventore dolores laborum delectus enim, libero doloremque magni deserunt modi possimus fugit repellat, cupiditate perspiciatis minima! Quaerat inventore delectus alias praesentium error sed blanditiis doloremque velit!</p>
 *     </ns-accordion-item>
 *     <ns-accordion-item title="Has Top Border" top-border>
 *       <p>some stuff</p>
 *     </ns-accordion-item>
 *     <ns-accordion-item title="Has Full Border" full-border>
 *       <p>some stuff</p>
 *     </ns-accordion-item>
 *     <ns-accordion-item title="Has No Border" no-border>
 *       <p>some stuff</p>
 *     </ns-accordion-item>
 *     <ns-accordion-item title="Shows An Icon For Completion" show-completed>
 *       <p>some stuff</p>
 *     </ns-accordion-item>
 *   </template>
 * </demo-snippet>
 *
 * <marked-element>
 *  <div slot="markdown-html">
 *  <script type="text/markdown">
 *
 *      ### Styling
 *
 *      ##### `<ns-accordion-item>` provides the following custom properties and mixins for styling:
 *
 *      Custom property                 | Description                           | Default
 *      ------------------------------- |---------------------------------------|-------------------
 *      `--ns-accordion-item`           | Mixin for item itself                 | `{}`
 *      `--ns-accordion-item-panel`     | Mixin for the surrounding panel       | `{}`
 *      `--ns-accordion-item-collapse`  | Mixin for the collapsable portion     | `{}`
 *      `--ns-accordion-item-head`      | Mixin for the head portion            | `{}`
 *
 *      ### Slots
 *
 *      ##### `<ns-accordion-item>` provides the following slots for content:
 *
 *      Custom property                 | Description                           | Default
 *      ------------------------------- |---------------------------------------|-------------------
 *      `completed-icon`                | To replace the default icon           | `check`
 *      `panel-title`                   | Overrides the title property          | `'Other Title'`
 *      `default`                       | Cotent for the collapsable area       | `''`
 *
 *  </script>
 * </marked-element>
 *
 */
export class NsAccordionItem extends PolymerElement {
  constructor() {
    super();
  }

  static get is() {
    return "ns-accordion-item";
  }

  static get template() {
    let htmlElement = document.createElement("TEMPLATE");
    htmlElement.innerHTML = template;
    return htmlElement;
  }

  static get properties() {
    return {
      /**
       * What level the accordion item is at. Options include:
       * `first` or `second`
       * First would be main accordion items, second would be sub-items
       */
      level: {
        type: String,
        value: "first",
        reflectToAttribute: true
      },
      /**
       * The title of the accordion
       */
      title: {
        type: String,
        value: "Sample Title"
      },
      /**
       * The rendered icon displayed next to the currently selected item
       */
      icon: {
        type: String,
        value: "nuskin-24:icon-narrow-angle-bottom"
      },
      /**
       * This icon will be used for accordions at the top level
       */
      level1Icon: {
        type: String,
        value: "nuskin-24:icon-narrow-angle-bottom"
      },
      /**
       * This icon will be used for accordions at the second level
       */
      level2Icon: {
        type: String,
        value: "nuskin:icon-triangle-down"
      },
      /**
       * Exposes the opened state of the accordion.
       */
      opened: {
        type: Boolean,
        value: false,
        reflectToAttribute: true
      },
      /**
       * Remove all borders from the item
       */
      noBorder: {
        type: Boolean,
        value: false,
        reflectToAttribute: true
      },
      /**
       * Add a top border to the item
       */
      topBorder: {
        type: Boolean,
        value: false,
        reflectToAttribute: true
      },
      /**
       * Add a full border around the item
       */
      fullBorder: {
        type: Boolean,
        value: false,
        reflectToAttribute: true
      },
      /**
       * Remove head border and reduces fluff
       */
      slim: {
        type: Boolean,
        value: false,
        reflectToAttribute: true
      },
      /**
       * Non-collapsable vertical column as desktop, horizontal and collapsable as mobile.
       */
      footer: {
        type: Boolean,
        value: false,
        reflectToAttribute: true
      },
      /**
       * Shows the checkmark for use in a stepped flow
       */
      showCompleted: {
        type: Boolean,
        value: false,
        notify: true
      },
      /**
       * Shows the actions slot
       */
      showActions: {
        type: Boolean,
        value: false,
        notify: true
      },
      /**
       * TODO: how to handle showing completed where completed is false
       * The completed state for the item
       */
      completed: {
        type: Boolean,
        value: false,
        notify: true
      }
    };
  }

  /**
   * Set the proper icon for indicating open status
   */
  ready() {
    super.ready();
    this.icon = this.level === "second" ? this.level2Icon : this.level1Icon;
    if (this.opened) this.open();
    this.$.panelHead.addEventListener("click", (e) => {
      this.toggle();
    });
  }

  /**
   * This method toggles the open/closed state of the item
   */
  toggle(e) {
    this.$.itemCollapse.toggle();
    this.opened = !this.opened;
    this.dispatchEvent(
      new CustomEvent("accordionitemtoggled", { bubbles: true, composed: true })
    );
  }

  /**
   * This method opens the item
   */
  open(e) {
    this.$.itemCollapse.show();
    this.opened = true;
  }

  /**
   * This method closes the item
   */
  close(e) {
    this.$.itemCollapse.hide();
    this.opened = false;
  }
}

customElements.define(NsAccordionItem.is, NsAccordionItem);
